
import Page from '@root/common/base/Page';
import { Section } from '@root/modules/frontpage/types/frontpage';
import FeedParser from '@root/common/components/helpers/FeedParser.vue';
import GridSkeletonLoader from '@root/common/components/base/loader/GridSkeletonLoader.vue';
import { feedService } from '@root/modules/frontpage/services/feed.service';
import { pageContentService } from '@root/modules/feed/services/pageContent.service';
import getFrontPageAdsSettings from '@root/views/frontpage/utils/getFrontPageAdsSettings';
import getChannelDomain from '@root/modules/channel/utils/getChannelDomain';
import rootConfig from '@root/config/root.config';

interface Data {
  feed: Section;
  type: string;
}

interface FeedVariables {
  id?: number | string;
  category?: {
    id: number;
  } | null;
  domain?: string;
  language: string;
  includeCategoryInfo?: boolean;
}

export default Page.extend<Data, unknown, unknown, unknown>({
  components: {
    FeedParser,
    GridSkeletonLoader,
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    next();
  },
  data() {
    return {
      feed: [],
      type: 'default',
    };
  },
  async fetch() {
    try {
      // Set frontpage type, if defined in config
      const { settings } = this.$channelConfig('page').frontpage;
      if (settings && settings.type) {
        this.type = settings.type;
      }

      const { domain, apiFeed, lang } = this.$channelConfig('settings');

      // Validate domain
      const validDomain = getChannelDomain(this.$route.params.channel || rootConfig.main_slug);
      if (!validDomain && this.$route.params.channel !== 'receptai') {
        this.handlePageError({ message: 'error.page_not_found', statusCode: 404 });
        return;
      }

      if (apiFeed) {
        const variables: FeedVariables = { language: lang.toUpperCase() };

        const id = settings && settings.id;
        if (id) {
          variables.id = id;
        } else {
          variables.domain = domain;
        }

        const feed = (await pageContentService.fetch(variables))?.feed;
        // Fetch feed in SSR
        // Fetch feed in CSR or fallback to the store data
        this.feed = feed || this.$store.getters['frontpage/getTempData'];
        this.$store.commit('frontpage/setTempData', this.feed);
      } else {
        const feed = (await feedService.fetch({ domain }))?.feed;
        // Fetch feed in SSR
        // Fetch feed in CSR or fallback to the store data
        this.feed = feed || this.$store.getters['frontpage/getTempData'];
        this.$store.commit('frontpage/setTempData', this.feed);
      }

      this.setPageAdsSettings(getFrontPageAdsSettings({ $channelConfig: this.$channelConfig }));
    } catch (e) {
      this.handlePageError(e);
    }
  },
  computed: {
    refetchHeadlines() {
      return this.$store.state.frontpage.refetch;
    },
  },
  watch: {
    refetchHeadlines(refetch) {
      if (refetch) {
        window.scrollTo(0, 0);
        this.$fetch();
        this.$store.commit('frontpage/setRefetchState', false);
      }
    },
  },
  head() {
    const settings = this.$channelConfig('meta').base;
    const meta = this.getPageMeta();
    meta.setTitle({ title: settings.title });

    return {
      title: meta.title,
      link: meta.link,
    };
  },
  mounted() {
    this.restoreScroll();
  },
});
